import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import {
  canadaWholesaleProducts,
  canadaWholesaleTiers,
  canadaWholesaleStats,
  canadaWholesaleCategories,
} from "@/mocks/canadaWholesale";
import CanadaFaqItem from "./components/CanadaFaqItem";

const CanadaWholesalePage = () => {
  const [formData, setFormData] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    province: "Ontario",
    businessType: "",
    orderValue: "",
    products: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");
  const [showFloating, setShowFloating] = useState(false);

  useEffect(() => {
    document.title = "Canada Factory Wholesale – Bulk Orders from CAD $6,000 | Crown Prime Mall";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Bulk orders direct from verified Canadian factories. Minimum CAD $6,000. Save 40–60% below retail on 400+ products. 2–4 day delivery across all Canadian provinces. Request a wholesale quote today.");
    }

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-canada-wholesale";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Canada Factory & Warehouse Wholesale – Crown Prime Mall",
      "description": "Bulk orders direct from Canadian factories. Minimum CAD $6,000 order. Save 40–60% below retail. 400+ products, 2–4 day delivery across all Canadian provinces.",
      "url": (import.meta.env.VITE_SITE_URL || "https://example.com") + "/canada-wholesale",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL || "https://example.com" },
          { "@type": "ListItem", "position": 2, "name": "Canada Wholesale", "item": (import.meta.env.VITE_SITE_URL || "https://example.com") + "/canada-wholesale" }
        ]
      },
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "CAD",
        "lowPrice": "6000",
        "offerCount": "400",
        "description": "Wholesale bulk orders from verified Canadian factories. Minimum CAD $6,000."
      }
    });
    if (!document.getElementById("schema-canada-wholesale")) {
      document.head.appendChild(script);
    }

    const scriptFaq = document.createElement("script");
    scriptFaq.type = "application/ld+json";
    scriptFaq.id = "schema-canada-wholesale-faq";
    scriptFaq.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is the minimum order for Canada wholesale?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The minimum wholesale order for Canadian buyers on Crown Prime Mall is CAD $6,000. This qualifies you for Bronze tier pricing with 40% savings off retail. Orders above CAD $15,000 unlock Silver tier, and CAD $50,000+ unlocks Gold tier with up to 60% savings."
          }
        },
        {
          "@type": "Question",
          "name": "How long does Canada wholesale delivery take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "All Canada wholesale orders ship from Canadian warehouses and are delivered within 2–4 business days to any Canadian province. There are no import duties or customs delays since products are already in Canada."
          }
        },
        {
          "@type": "Question",
          "name": "Are the Canadian factory suppliers verified?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Every Canadian supplier on Crown Prime Mall is verified and certified. All products meet Canadian safety standards including Health Canada, CSA, and other relevant certifications depending on the product category."
          }
        },
        {
          "@type": "Question",
          "name": "What payment methods are accepted for Canada wholesale orders?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We accept secure CAD payments via bank transfer, PayPal, Visa, Mastercard, and American Express. All transactions are SSL-encrypted for your security."
          }
        },
        {
          "@type": "Question",
          "name": "How quickly will I receive a Canada wholesale price quote?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our Canada wholesale team responds to all quote requests within 2 business hours. Simply fill out the quote form with your product selection and estimated order value, and we will send you a custom CAD pricing breakdown."
          }
        },
        {
          "@type": "Question",
          "name": "Which Canadian provinces do you deliver to?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We deliver wholesale orders to all Canadian provinces including Ontario, Quebec, British Columbia, Alberta, Manitoba, Saskatchewan, Nova Scotia, New Brunswick, and more. All shipments originate from Canadian warehouses with no cross-border delays."
          }
        },
        {
          "@type": "Question",
          "name": "Can I mix products from different categories in one Canada wholesale order?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. You can combine products from different categories — farm products, garden, home goods, beauty, and more — in a single wholesale order, as long as the total order value meets the CAD $6,000 minimum."
          }
        }
      ]
    });
    if (!document.getElementById("schema-canada-wholesale-faq")) {
      document.head.appendChild(scriptFaq);
    }

    return () => {
      const el = document.getElementById("schema-canada-wholesale");
      if (el) el.remove();
      const el2 = document.getElementById("schema-canada-wholesale-faq");
      if (el2) el2.remove();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowFloating(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const body = new URLSearchParams();
    Object.entries(formData).forEach(([k, v]) => body.append(k, v));
    try {
      await fetch("https://readdy.ai/api/form/d7df1imm3fsfopf23rd0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch {
      // silent
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  const filteredProducts =
    activeCategory === "All"
      ? canadaWholesaleProducts
      : canadaWholesaleProducts.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#0a1a0a] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/bda211dd419dae496d9e1cf8a9273c19.jpg"
            alt="Canada Wholesale Warehouse"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1a0a] via-[#0a1a0a]/80 to-transparent"></div>
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#C41E3A]/20 border border-[#C41E3A]/40 text-[#C41E3A] text-xs font-bold px-4 py-2 rounded-full mb-5">
              <span>🇨🇦</span>
              CANADA FACTORY &amp; WAREHOUSE WHOLESALE
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5">
              Bulk Orders Direct<br />
              from <span className="text-[#D4AF37]">Canada Factories</span>
            </h1>
            <p className="text-white/70 text-sm leading-relaxed mb-8 max-w-xl">
              Minimum order <strong className="text-white">CAD $6,000</strong> — unlock wholesale pricing up to 60% below retail. Verified Canadian factories and local warehouses. Fast 2–4 day delivery across Canada.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#canada-quote-form"
                className="flex items-center gap-2 bg-[#C41E3A] text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i> Request Wholesale Quote
              </a>
              <a
                href="https://wa.me/1234567890"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-[#1ebe5d] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#C41E3A] py-8">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {canadaWholesaleStats.map((s) => (
              <div key={s.label}>
                <div className="text-2xl font-extrabold text-white">{s.value}</div>
                <div className="text-white/70 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges Strip */}
      <section className="bg-white border-b border-gray-100 py-5">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {[
              { icon: "ri-heart-pulse-line", label: "Health Canada Certified", color: "text-red-600", bg: "bg-red-50", border: "border-red-100" },
              { icon: "ri-shield-star-line", label: "CSA Approved", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-100" },
              { icon: "ri-pass-valid-line", label: "No Import Duties", color: "text-green-700", bg: "bg-green-50", border: "border-green-100" },
              { icon: "ri-bank-card-line", label: "CAD Payment Accepted", color: "text-[#C41E3A]", bg: "bg-[#C41E3A]/5", border: "border-[#C41E3A]/15" },
              { icon: "ri-truck-line", label: "2–4 Day Canada Delivery", color: "text-gray-700", bg: "bg-gray-50", border: "border-gray-200" },
              { icon: "ri-verified-badge-line", label: "Verified Canadian Factories", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-100" },
            ].map((badge) => (
              <div
                key={badge.label}
                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-full border ${badge.bg} ${badge.border}`}
              >
                <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 ${badge.color}`}>
                  <i className={`${badge.icon} text-base`}></i>
                </div>
                <span className={`text-xs font-semibold whitespace-nowrap ${badge.color}`}>{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-[#f8f7f5]">
        <div className="max-w-[1400px] mx-auto px-6">
          <h2 className="text-2xl font-extrabold text-gray-900 text-center mb-10">How Canada Wholesale Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", icon: "ri-search-2-line", title: "Browse Products", desc: "Explore 400+ Canadian factory products across farm food, garden, home goods, beauty and more." },
              { step: "02", icon: "ri-file-list-3-line", title: "Submit Quote (CAD $6K+)", desc: "Fill out the quote form with your product selection and quantity. Minimum CAD $6,000 per order." },
              { step: "03", icon: "ri-shake-hands-line", title: "Get Wholesale Price", desc: "Our team confirms your wholesale pricing within 2 hours. Save 40–60% off retail." },
              { step: "04", icon: "ri-truck-line", title: "Fast Canada Delivery", desc: "Products ship from Canadian warehouses. 2–4 day delivery across all provinces. No import duties." },
            ].map((item) => (
              <div key={item.step} className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#C41E3A] rounded-full text-white text-xs font-extrabold flex-shrink-0">{item.step}</div>
                  <div className="w-9 h-9 flex items-center justify-center bg-[#C41E3A]/10 rounded-xl">
                    <i className={item.icon + " text-[#C41E3A] text-lg"}></i>
                  </div>
                </div>
                <div className="font-bold text-gray-900 text-sm mb-2">{item.title}</div>
                <div className="text-xs text-gray-500 leading-relaxed">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Canada Wholesale Pricing Tiers</h2>
            <p className="text-gray-500 text-sm">The more you order, the more you save</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {canadaWholesaleTiers.map((tier) => (
              <div
                key={tier.name}
                className={`relative rounded-2xl border-2 p-7 ${tier.bgColor} ${tier.borderColor} ${tier.popular ? "scale-105" : ""}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#1a0a0a] text-[10px] font-extrabold px-4 py-1 rounded-full whitespace-nowrap">
                    MOST POPULAR
                  </div>
                )}
                <div className={`text-sm font-extrabold mb-1 flex items-center gap-2 ${tier.textColor}`}>
                  <i className="ri-vip-crown-fill"></i>{tier.name} Tier
                </div>
                <div className="text-2xl font-extrabold text-gray-900 mb-1">{tier.discount}</div>
                <div className="text-xs text-gray-500 mb-5">
                  Min. <strong>CAD ${tier.minOrder.toLocaleString()}</strong>
                  {tier.maxOrder ? ` – $${tier.maxOrder.toLocaleString()}` : "+"}
                </div>
                <ul className="space-y-2.5 mb-6">
                  {tier.perks.map((perk) => (
                    <li key={perk} className={`flex items-start gap-2 text-sm text-gray-700`}>
                      <i className={`ri-checkbox-circle-fill text-sm mt-0.5 flex-shrink-0 ${tier.textColor}`}></i>
                      {perk}
                    </li>
                  ))}
                </ul>
                <a
                  href="#canada-quote-form"
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap bg-gradient-to-r ${tier.color} text-white hover:opacity-90`}
                >
                  Get {tier.name} Pricing
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16 bg-[#f8f7f5]">
        <div className="max-w-[1400px] mx-auto px-6">
          <h2 className="text-2xl font-extrabold text-gray-900 mb-8">Canada Wholesale Product Categories</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-12">
            {canadaWholesaleCategories.map((cat) => (
              <div
                key={cat.id}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden group cursor-pointer hover:border-[#C41E3A]/40 transition-all"
              >
                <div className="w-full h-36 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <i className={cat.icon + " text-[#C41E3A] text-base"}></i>
                    <span className="text-sm font-bold text-gray-900">{cat.name}</span>
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {cat.productCount} products · Min CAD ${cat.minOrder.toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Product Filter */}
          <div className="flex gap-2 flex-wrap mb-6">
            {["All", "Farm Products", "Garden", "Home Goods", "Beauty"].map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-[#C41E3A] text-white border-[#C41E3A]"
                    : "border-gray-200 text-gray-600 hover:border-[#C41E3A] bg-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-[#D4AF37]/40 transition-all group"
              >
                <div className="relative w-full h-44 overflow-hidden bg-gray-50">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-2 left-2 bg-[#C41E3A] text-white text-[10px] font-bold px-2 py-1 rounded-full">
                    {product.badge}
                  </div>
                  <div className="absolute top-2 right-2 bg-green-600 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                    Save {product.savings}%
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-[10px] text-gray-400 mb-1">
                    {product.flag} {product.factory} — {product.location}
                  </div>
                  <div className="text-sm font-bold text-gray-900 mb-3">{product.name}</div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 bg-gray-50 rounded-lg p-2.5 text-center">
                      <div className="text-[10px] text-gray-400">Retail</div>
                      <div className="text-sm font-bold text-gray-400 line-through">${product.retailPrice.toFixed(2)}</div>
                    </div>
                    <i className="ri-arrow-right-line text-gray-300"></i>
                    <div className="flex-1 bg-[#C41E3A]/10 rounded-lg p-2.5 text-center border border-[#C41E3A]/20">
                      <div className="text-[10px] text-[#C41E3A] font-semibold">Wholesale</div>
                      <div className="text-sm font-extrabold text-[#C41E3A]">${product.wholesalePrice.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mb-3">
                    Min. order: <strong>{product.minOrderQty} units</strong> (≈ CAD ${product.minOrderValue.toLocaleString()})
                  </div>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {product.certifications.map((c) => (
                      <span
                        key={c}
                        className="text-[9px] font-semibold bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded-full"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                  <a
                    href="#canada-quote-form"
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#C41E3A] text-white rounded-xl text-sm font-semibold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-file-list-3-line"></i> Request Quote
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="canada-quote-form" className="py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Info */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#C41E3A]/10 text-[#C41E3A] text-xs font-bold px-4 py-2 rounded-full mb-5">
                <i className="ri-file-list-3-line"></i>
                CANADA WHOLESALE QUOTE REQUEST
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
                Get Your Canada Wholesale<br />Price Quote
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                Fill out the form and our Canada wholesale team will respond within 2 hours with your custom pricing. Minimum order CAD $6,000.
              </p>
              <div className="space-y-4">
                {[
                  { icon: "ri-time-line", title: "2-Hour Response", desc: "Our Canada wholesale team responds to all quote requests within 2 business hours." },
                  { icon: "ri-shield-check-line", title: "Verified Canadian Factories", desc: "All suppliers are verified and certified. Products meet Canadian safety standards." },
                  { icon: "ri-truck-line", title: "2–4 Day Canada Delivery", desc: "Ships from Canadian warehouses. No import duties, no customs delays." },
                  { icon: "ri-bank-card-line", title: "Secure CAD Payment", desc: "Pay securely in CAD via bank transfer, PayPal, or credit card." },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C41E3A]/10 rounded-xl flex-shrink-0">
                      <i className={item.icon + " text-[#C41E3A] text-lg"}></i>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-gray-900">{item.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Canada Provinces Quick Info */}
              <div className="mt-8 bg-[#f8f7f5] rounded-2xl p-5 border border-gray-100">
                <div className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <span>🇨🇦</span> We Ship to All Canadian Provinces
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {["Ontario", "Quebec", "British Columbia", "Alberta", "Manitoba", "Saskatchewan", "Nova Scotia", "New Brunswick"].map((prov) => (
                    <div key={prov} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <i className="ri-checkbox-circle-fill text-green-500 text-xs"></i>
                      {prov}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Form */}
            <div className="bg-[#f8f7f5] rounded-2xl p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-fill text-green-600 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Quote Request Submitted!</h3>
                  <p className="text-gray-500 text-sm mb-6">
                    Our Canada wholesale team will contact you within 2 hours with your custom pricing.
                  </p>
                  <a
                    href="https://wa.me/1234567890"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-[#1ebe5d] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line"></i> Chat on WhatsApp
                  </a>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="canada-wholesale-quote-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <h3 className="text-base font-bold text-gray-900 mb-5">Canada Wholesale Quote Request</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Company Name *</label>
                      <input
                        type="text"
                        name="companyName"
                        required
                        value={formData.companyName}
                        onChange={(e) => handleChange("companyName", e.target.value)}
                        placeholder="Your company"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Contact Name *</label>
                      <input
                        type="text"
                        name="contactName"
                        required
                        value={formData.contactName}
                        onChange={(e) => handleChange("contactName", e.target.value)}
                        placeholder="Your name"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="your@company.com"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none bg-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Phone / WhatsApp</label>
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="+1 (416) 000-0000"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Province *</label>
                      <select
                        name="province"
                        required
                        value={formData.province}
                        onChange={(e) => handleChange("province", e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none bg-white cursor-pointer"
                      >
                        <option value="Ontario">Ontario</option>
                        <option value="Quebec">Quebec</option>
                        <option value="British Columbia">British Columbia</option>
                        <option value="Alberta">Alberta</option>
                        <option value="Manitoba">Manitoba</option>
                        <option value="Saskatchewan">Saskatchewan</option>
                        <option value="Nova Scotia">Nova Scotia</option>
                        <option value="New Brunswick">New Brunswick</option>
                        <option value="Other">Other Province</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Business Type *</label>
                    <select
                      name="businessType"
                      required
                      value={formData.businessType}
                      onChange={(e) => handleChange("businessType", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none bg-white cursor-pointer"
                    >
                      <option value="">Select type...</option>
                      <option value="Retailer">Retailer / Store</option>
                      <option value="Distributor">Distributor / Wholesaler</option>
                      <option value="E-commerce">E-commerce Seller</option>
                      <option value="Dropshipper">Dropshipper</option>
                      <option value="Reseller">Reseller / Influencer</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Estimated Order Value (CAD) *</label>
                    <select
                      name="orderValue"
                      required
                      value={formData.orderValue}
                      onChange={(e) => handleChange("orderValue", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none bg-white cursor-pointer"
                    >
                      <option value="">Select range...</option>
                      <option value="CAD $6,000 – $14,999">CAD $6,000 – $14,999 (Bronze)</option>
                      <option value="CAD $15,000 – $49,999">CAD $15,000 – $49,999 (Silver)</option>
                      <option value="CAD $50,000+">CAD $50,000+ (Gold)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Products Interested In</label>
                    <input
                      type="text"
                      name="products"
                      value={formData.products}
                      onChange={(e) => handleChange("products", e.target.value)}
                      placeholder="e.g. Maple syrup, Blueberry jam, Cedar beds..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Additional Notes</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      placeholder="Any specific requirements, delivery timeline, or questions..."
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none bg-white resize-none"
                    />
                    <div className="text-right text-xs text-gray-400 mt-1">{formData.message.length}/500</div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 py-4 bg-[#C41E3A] text-white rounded-xl text-sm font-bold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {submitting ? (
                      <><i className="ri-loader-4-line animate-spin"></i> Submitting...</>
                    ) : (
                      <><i className="ri-send-plane-line"></i> Submit Quote Request</>
                    )}
                  </button>
                  <p className="text-[10px] text-gray-400 text-center">
                    By submitting, you agree to our Terms of Service. We respond within 2 business hours.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-[#f8f7f5]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-[#C41E3A]/10 text-[#C41E3A] text-xs font-bold px-4 py-2 rounded-full mb-4">
                <i className="ri-question-answer-line"></i>
                FREQUENTLY ASKED QUESTIONS
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Canada Wholesale FAQ</h2>
              <p className="text-gray-500 text-sm">Everything Canadian buyers need to know about bulk ordering from Canadian factories</p>
            </div>
            <div className="space-y-3">
              {[
                {
                  q: "What is the minimum order for Canada wholesale?",
                  a: "The minimum wholesale order for Canadian buyers on Crown Prime Mall is CAD $6,000. This qualifies you for Bronze tier pricing with 40% savings off retail. Orders above CAD $15,000 unlock Silver tier, and CAD $50,000+ unlocks Gold tier with up to 60% savings."
                },
                {
                  q: "How long does Canada wholesale delivery take?",
                  a: "All Canada wholesale orders ship from Canadian warehouses and are delivered within 2–4 business days to any Canadian province. There are no import duties or customs delays since products are already in Canada."
                },
                {
                  q: "Are the Canadian factory suppliers verified?",
                  a: "Yes. Every Canadian supplier on Crown Prime Mall is verified and certified. All products meet Canadian safety standards including Health Canada, CSA, and other relevant certifications depending on the product category."
                },
                {
                  q: "What payment methods are accepted for Canada wholesale orders?",
                  a: "We accept secure CAD payments via bank transfer, PayPal, Visa, Mastercard, and American Express. All transactions are SSL-encrypted for your security."
                },
                {
                  q: "How quickly will I receive a Canada wholesale price quote?",
                  a: "Our Canada wholesale team responds to all quote requests within 2 business hours. Simply fill out the quote form with your product selection and estimated order value, and we will send you a custom CAD pricing breakdown."
                },
                {
                  q: "Which Canadian provinces do you deliver to?",
                  a: "We deliver wholesale orders to all Canadian provinces including Ontario, Quebec, British Columbia, Alberta, Manitoba, Saskatchewan, Nova Scotia, New Brunswick, and more. All shipments originate from Canadian warehouses with no cross-border delays."
                },
                {
                  q: "Can I mix products from different categories in one Canada wholesale order?",
                  a: "Yes. You can combine products from different categories — farm products, garden, home goods, beauty, and more — in a single wholesale order, as long as the total order value meets the CAD $6,000 minimum."
                }
              ].map((item, i) => (
                <CanadaFaqItem key={i} question={item.q} answer={item.a} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Floating Request Quote Button */}
      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
          showFloating ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <a
          href="#canada-quote-form"
          className="flex items-center gap-2.5 bg-[#C41E3A] text-white px-5 py-3.5 rounded-full text-sm font-bold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
          style={{ boxShadow: "0 4px 20px rgba(196,30,58,0.35)" }}
        >
          <i className="ri-file-list-3-line text-base"></i>
          Request Quote
        </a>
      </div>

      <Footer />
    </div>
  );
};

export default CanadaWholesalePage;
